import {
    commonReducer,
    initialCommonState,
    type commonState,
    type commonAction,
} from "@/hooks/reducers/common.reducer";
import type { parsedApiError } from "@/types/api/errors.api";
import type {
    competence,
    question,
    test,
    thematicArea,
} from "./types.api";
import type { simulation } from "../dashboard/types.api";

export interface configState extends commonState {
    simulation: simulation | null;
    thematicAreas: thematicArea[];
    competences: competence[];
    selectedSessionId: number | null;
    selectedTestId: number | null;
    activeTest: test | null;
    validationErrors: Record<number, any>;
    isSaving: boolean;
}

export type configAction =
    | commonAction
    | {
        type: "SET_INITIAL_DATA";
        payload: {
            simulation: simulation;
            thematicAreas: thematicArea[];
            competences: competence[];
        };
    }
    | { type: "SELECT_SESSION"; payload: number | null }
    | { type: "SELECT_TEST"; payload: number | null }
    | { type: "SET_ACTIVE_TEST"; payload: test | null }
    | {
        type: "UPDATE_ACTIVE_TEST_SETTINGS";
        payload: Partial<Pick<test, "weights">>;
    }
    | {
        type: "UPDATE_QUESTION_OBJECT";
        payload: { questionIndex: number; updatedQuestion: question };
    }
    | { type: "SET_VALIDATION_ERRORS"; payload: Record<number, any> }
    | { type: "SAVE_START" }
    | { type: "SAVE_SUCCESS"; payload: test }
    | { type: "SAVE_FAILURE"; payload: parsedApiError };

export const createInitialState = (): configState => ({
    ...initialCommonState,
    isLoading: true,
    isSaving: false,
    simulation: null,
    thematicAreas: [],
    competences: [],
    selectedSessionId: null,
    selectedTestId: null,
    activeTest: null,
    validationErrors: {},
});

export const configReducer = (
    state: configState,
    action: configAction,
): configState => {
    const commonState = commonReducer(state, action as commonAction);
    if (commonState !== state) return { ...state, ...commonState };

    switch (action.type) {
        case "SET_INITIAL_DATA": {
            const firstSessionId = action.payload.simulation.sessions?.[0]?.id || null;
            return {
                ...state,
                isLoading: false,
                error: null,
                simulation: action.payload.simulation,
                thematicAreas: action.payload.thematicAreas,
                competences: action.payload.competences,
                selectedSessionId: firstSessionId,
            };
        }
        case "SELECT_SESSION":
            return {
                ...state,
                selectedSessionId: action.payload,
                selectedTestId: null,
                activeTest: null,
            };
        case "SELECT_TEST":
            return { ...state, selectedTestId: action.payload };
        case "SET_ACTIVE_TEST":
            return {
                ...state,
                isLoading: false,
                activeTest: action.payload,
                validationErrors: {},
            };
        case "UPDATE_ACTIVE_TEST_SETTINGS":
            if (!state.activeTest) return state;
            return {
                ...state,
                activeTest: { ...state.activeTest, ...action.payload },
            };
        case "UPDATE_QUESTION_OBJECT": {
            if (!state.activeTest) return state;
            const { questionIndex, updatedQuestion } = action.payload;
            const updatedQuestions = [...state.activeTest.questions];
            updatedQuestions[questionIndex] = updatedQuestion;
            return {
                ...state,
                activeTest: {
                    ...state.activeTest,
                    questions: updatedQuestions,
                },
            };
        }
        case "SET_VALIDATION_ERRORS":
            return { ...state, validationErrors: action.payload };
        case "SAVE_START":
            return { ...state, isSaving: true, error: null };
        case "SAVE_SUCCESS": {
            if (!state.simulation) return { ...state, isSaving: false };
            const updatedSessions = state.simulation.sessions?.map((session) => {
                const testIndex = session.tests.findIndex(
                    (t) => t.id === action.payload.id,
                );
                if (testIndex === -1) {
                    return session;
                }
                const updatedTests = [...session.tests];
                updatedTests[testIndex] = action.payload;
                return { ...session, tests: updatedTests };
            });
            return {
                ...state,
                isSaving: false,
                simulation: { ...state.simulation, sessions: updatedSessions },
                activeTest: action.payload,
            };
        }
        case "SAVE_FAILURE":
            return { ...state, isSaving: false, error: action.payload };
        default:
            return state;
    }
};
